import { useEffect, useRef, useState } from 'react';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';

// Long enough for gunicorn's --timeout 120 to have reclaimed the worker that
// DigitalOcean's edge gave up on at ~60s. Shorter would re-open the door on a
// request the server has not finished.
const COOLDOWN_MS = 20000;

// Statuses that mean "the server may still be working on this", as opposed to a
// final answer. A thrown fetch with no status is a dead connection, which is the
// same situation.
const STILL_WORKING = [502, 503, 504];

function isStillWorking(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === undefined ? true : STILL_WORKING.includes(status);
}

/**
 * The Bridge Club "add a card" flow (HelcimPay.js verify -> vault), extracted from
 * BridgeClubProfile so the onboarding card step reuses the SAME debugged handler instead
 * of reimplementing it. Every hard-won detail is preserved verbatim:
 *   - the two-domain origin gate (Helcim's modal posts from secure.helcim.APP, not .com),
 *     matched explicitly and never as a bare 'helcim' substring;
 *   - reading the real transaction status from the nested parsed.data.data.status
 *     ('APPROVED') rather than the top-level HTTP 200, with old shapes as a fallback;
 *   - forwarding parsed.data to confirm UNMUTATED (any spread / re-key / re-serialize
 *     breaks the response hash);
 *   - tearing down the HelcimPay overlay on BOTH success and failure — a lingering iframe
 *     sits on top of the page and swallows every subsequent click.
 *
 * The container owns what happens AFTER a card is saved: onAdded refreshes ITS card list
 * (the profile's saved-cards call and the onboarding card-step call differ), onError
 * surfaces the message. addCardInitialize / addCardConfirm are member-scoped, so the same
 * hook works on the profile page and inside the onboarding shell.
 */
export function useHelcimAddCard(opts: {
  onAdded: () => void | Promise<void>;
  onError?: (message: string) => void;
}) {
  const { onAdded, onError } = opts;
  const [adding, setAdding] = useState(false);
  const tokenRef = useRef<string | null>(null);
  // A SECOND CLICK IS NOT A FREE RETRY. The button is disabled while a request
  // is in flight, but that guard ends when the response arrives - and a 504
  // from DigitalOcean's edge arrives at ~60s while the WORKER IS STILL RUNNING,
  // for up to gunicorn's 120s timeout. Re-enabling then invites a click that
  // starts a second long request beside the first. The app runs 2 workers, so
  // two clicks is the whole app. After a timeout-shaped failure the button
  // stays down until the server has plausibly let go.
  const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (cooldownRef.current) clearTimeout(cooldownRef.current);
  }, []);
  // Hold the latest callbacks in refs so the message listener never re-subscribes (and so
  // a stale closure can't fire against an old container state).
  const onAddedRef = useRef(onAdded);
  const onErrorRef = useRef(onError);
  onAddedRef.current = onAdded;
  onErrorRef.current = onError;

  useEffect(() => {
    const handleAddCardMessage = async (event: MessageEvent) => {
      // Accept Helcim's two domains — the modal posts from secure.helcim.app, not .com.
      // Both matched explicitly; never a bare 'helcim' substring (a hostile
      // 'helcim.attacker.com' would satisfy that).
      if (!(event.origin.includes('helcim.com') || event.origin.includes('helcim.app'))) return;
      const data: any = event.data;
      if (!data || typeof data !== 'object') return;
      if (typeof data.eventMessage !== 'string') return;

      let parsed: any;
      try {
        parsed = JSON.parse(data.eventMessage);
      } catch {
        return;
      }

      // The real transaction status is nested at parsed.data.data.status ('APPROVED'); the
      // top-level `status` is the HTTP 200. Read the nested path FIRST, keep the old shapes
      // as a fallback. eventName is a dynamic 'helcim-pay-js-<checkoutToken>' string, so we
      // classify on status, NOT the name.
      const statusStr = String(
        parsed?.data?.data?.status ||
        parsed?.eventStatus || parsed?.status || parsed?.data?.status || ''
      ).toUpperCase();
      const isSuccess =
        statusStr === 'APPROVED' || statusStr === 'SUCCESS' || statusStr === 'COMPLETED';

      const token = tokenRef.current;
      if (!token) return; // not our add-card flow

      if (!isSuccess) {
        // Failure OR unmatched — never leave the button frozen on "Starting…". Tear down
        // the overlay first; otherwise it lingers on top and swallows every click.
        window.removeHelcimPayIframe?.();
        onErrorRef.current?.('We could not verify that card. Please try again.');
        setAdding(false);
        tokenRef.current = null;
        return;
      }

      try {
        // Forward parsed.data — the object carrying BOTH `hash` and the signed inner
        // `data`. Unmutated (no spread / re-key / re-serialize).
        await bridgeClubAPI.addCardConfirm({ checkout_token: token, response: parsed.data });
        await onAddedRef.current();
      } catch (err: any) {
        onErrorRef.current?.(err?.error || 'We could not verify that card. Please try again.');
      } finally {
        window.removeHelcimPayIframe?.();
        setAdding(false);
        tokenRef.current = null;
      }
    };

    window.addEventListener('message', handleAddCardMessage);
    return () => window.removeEventListener('message', handleAddCardMessage);
  }, []);

  const startAddCard = async () => {
    setAdding(true);
    try {
      const res = await bridgeClubAPI.addCardInitialize();
      const token = res?.checkout_token;
      if (!token) {
        onErrorRef.current?.('Could not start card verification. Please try again.');
        setAdding(false);
        return;
      }
      tokenRef.current = token;
      if (typeof window.appendHelcimPayIframe === 'function') {
        // Clear any stale overlay from a prior attempt before opening a new one.
        window.removeHelcimPayIframe?.();
        window.appendHelcimPayIframe(token);
      } else {
        onErrorRef.current?.('Payment form is unavailable. Please refresh and try again.');
        setAdding(false);
        tokenRef.current = null;
      }
    } catch (err: any) {
      // A GATEWAY TIMEOUT IS NOT A DECLINE. 502/503/504 and a dead connection
      // all mean the server may still be working on this; anything else (a
      // validation error, a 401) is final and the customer can act at once.
      const held = isStillWorking(err);
      onErrorRef.current?.(err?.error
        || (held
          ? 'That took too long to start. Give it a moment and try again.'
          : 'Could not start card verification. Please try again.'));
      if (held) {
        cooldownRef.current = setTimeout(() => setAdding(false), COOLDOWN_MS);
      } else {
        setAdding(false);
      }
    }
  };

  return { startAddCard, adding };
}
